import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReAuth } from '@/api/baseApi';
import { rolesTags } from '@/api/roles/tags';
import {
  PERMISSIONS_API,
  ROLES_API,
  ROLES_WITH_PERMISSIONS_API,
} from '@/shared/api/constants';
import { PageableResponse } from '@/shared/api/types';
import { mapSortToQueryParameter } from '@/utils/utils';

import type {
  GetPermissionsPayload,
  GetRolesPayload,
  PermissionResponse,
  RoleRequest,
  RoleResponse,
  RoleWithPermissionsResponse,
  UpdateRolePayload,
} from './types';

export const rolesApi = createApi({
  reducerPath: 'rolesApi',
  baseQuery: baseQueryWithReAuth,
  tagTypes: Object.values(rolesTags),
  endpoints: (builder) => ({
    getRoles: builder.query<
      PageableResponse<RoleResponse> | RoleResponse[],
      GetRolesPayload
    >({
      query: (payload) => ({
        url: ROLES_API,
        method: 'GET',
        params: {
          ...payload?.filters,
          page: payload?.pageable?.page,
          size: payload?.pageable?.size,
          sort: mapSortToQueryParameter(payload?.sort),
        },
      }),
      providesTags: [rolesTags.ROLES],
    }),
    getRolesWithPermissions: builder.query<
      PageableResponse<RoleWithPermissionsResponse> | RoleWithPermissionsResponse[],
      GetRolesPayload
    >({
      query: (payload) => ({
        url: ROLES_WITH_PERMISSIONS_API,
        method: 'GET',
        params: {
          ...payload?.filters,
          page: payload?.pageable?.page,
          size: payload?.pageable?.size,
          sort: mapSortToQueryParameter(payload?.sort),
        },
      }),
      providesTags: [rolesTags.ROLES],
    }),

    getRoleByName: builder.query<RoleResponse, string>({
      query: (roleName) => ({
        url: `${ROLES_API}/${encodeURIComponent(roleName)}`,
        method: 'GET',
      }),
      providesTags: [rolesTags.ROLE_RECORD],
    }),

    getRolePermissions: builder.query<PermissionResponse[], string>({
      query: (roleName) => ({
        url: `${ROLES_API}/${encodeURIComponent(roleName)}/permissions`,
        method: 'GET',
      }),
      providesTags: [rolesTags.ROLE_RECORD, rolesTags.PERMISSIONS],
    }),

    createRole: builder.mutation<any, RoleRequest>({
      query: (payload) => ({
        url: ROLES_API,
        method: 'POST',
        headers: {
          Accept: 'text/plain',
        },
        responseHandler: 'text',
        body: payload,
      }),
      invalidatesTags: [rolesTags.ROLES],
    }),

    updateRole: builder.mutation<void, UpdateRolePayload>({
      query: ({ roleName, ...payload }) => ({
        url: `${ROLES_API}/${encodeURIComponent(roleName)}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: [rolesTags.ROLES, rolesTags.ROLE_RECORD],
    }),

    deleteRole: builder.mutation<void, string>({
      query: (roleName) => ({
        url: `${ROLES_API}/${encodeURIComponent(roleName)}`,
        method: 'DELETE',
      }),
      invalidatesTags: [rolesTags.ROLES],
    }),
    getPermissions: builder.query<PermissionResponse[], GetPermissionsPayload>({
      query: (payload) => ({
        url: PERMISSIONS_API,
        method: 'GET',
        params: {
          ...payload?.filters,
          page: payload?.pageable?.page,
          size: payload?.pageable?.size,
          sort: mapSortToQueryParameter(payload?.sort),
        },
      }),
      transformResponse: (
        response: PageableResponse<PermissionResponse> | PermissionResponse[],
      ) => {
        return Array.isArray(response) ? response : response?.content;
      },
      providesTags: [rolesTags.PERMISSIONS],
    }),
  }),
});

export const {
  useGetRolesQuery,
  useGetRolesWithPermissionsQuery,
  useGetRoleByNameQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useGetRolePermissionsQuery,
  useDeleteRoleMutation,
  useGetPermissionsQuery,
} = rolesApi;
